import { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import type {
  ImageStorageSettings,
  ImageStorageSettingsCreate,
  ImageStorageTestResponse,
} from '../types';

// Self-serve provider guide. "S3" is a de-facto standard, so any of these work
// with the same endpoint_url + bucket + key/secret form.
const PROVIDERS: {
  name: string;
  signupUrl: string;
  pricing: string;
  endpointHint: string;
  recommended?: boolean;
}[] = [
  {
    name: 'Cloudflare R2',
    signupUrl: 'https://dash.cloudflare.com/sign-up',
    pricing:
      '$0.015/GB-mo storage, $0 egress, 10 GB free. Best for images served repeatedly - pair with a custom domain for free CDN delivery.',
    endpointHint: 'https://<account-id>.r2.cloudflarestorage.com',
    recommended: true,
  },
  {
    name: 'Backblaze B2',
    signupUrl: 'https://www.backblaze.com/sign-up/cloud-storage',
    pricing:
      '$0.006/GB-mo (cheapest storage); free egress up to 3× stored data, then $0.01/GB. Free egress via Cloudflare.',
    endpointHint: 'https://s3.<region>.backblazeb2.com',
  },
  {
    name: 'DigitalOcean Spaces',
    signupUrl: 'https://www.digitalocean.com/products/spaces',
    pricing:
      'Flat $5/mo includes 250 GB storage + 1 TB transfer + built-in CDN; $0.02/GB over. Simplest all-in pricing.',
    endpointHint: 'https://<region>.digitaloceanspaces.com',
  },
  {
    name: 'AWS S3',
    signupUrl: 'https://aws.amazon.com/s3/',
    pricing:
      '~$0.023/GB-mo storage + $0.09/GB egress - egress makes it costly for image delivery. Pick only if already on AWS. Leave endpoint blank.',
    endpointHint: '(leave blank - uses AWS default)',
  },
  {
    name: 'MinIO (self-host)',
    signupUrl: 'https://min.io/',
    pricing: 'Free / open-source; you run it. For users who already have infra.',
    endpointHint: 'https://minio.your-domain.com',
  },
];

const EMPTY_FORM: ImageStorageSettingsCreate = {
  name: '',
  endpoint_url: '',
  region: 'auto',
  bucket: '',
  access_key_id: '',
  secret_access_key: '',
  public_base_url: '',
  is_enabled: true,
  optimize_uploads: true,
  image_format: 'webp',
  image_quality: 82,
  variant_widths: [480, 768, 1024, 1536],
};

export default function ImageStorageSettingsPage() {
  const [items, setItems] = useState<ImageStorageSettings[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [form, setForm] = useState<ImageStorageSettingsCreate>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [testing, setTesting] = useState<Record<number, boolean>>({});
  const [testResults, setTestResults] = useState<
    Record<number, ImageStorageTestResponse>
  >({});

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.getImageStorageSettings();
      setItems(data?.items || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load storage settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (s: ImageStorageSettings) => {
    setForm({
      name: s.name ?? '',
      endpoint_url: s.endpoint_url ?? '',
      region: s.region ?? 'auto',
      bucket: s.bucket,
      access_key_id: s.access_key_id,
      secret_access_key: '', // keep existing unless replaced
      public_base_url: s.public_base_url ?? '',
      is_enabled: s.is_enabled,
      optimize_uploads: s.optimize_uploads,
      image_format: s.image_format ?? 'webp',
      image_quality: s.image_quality ?? 82,
      variant_widths: s.variant_widths ?? [480, 768, 1024, 1536],
    });
    setEditingId(s.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.bucket.trim() || !form.access_key_id.trim()) {
      setError('Bucket and Access key ID are required.');
      return;
    }
    if (editingId === null && !form.secret_access_key.trim()) {
      setError('Secret access key is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingId === null) {
        await api.createImageStorageSettings(form);
      } else {
        // Omit the secret when blank so the stored one is preserved.
        const { secret_access_key, ...rest } = form;
        await api.updateImageStorageSettings(editingId, {
          ...rest,
          ...(secret_access_key.trim() ? { secret_access_key } : {}),
        });
      }
      setShowForm(false);
      fetchItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (s: ImageStorageSettings) => {
    setTesting((cur) => ({ ...cur, [s.id]: true }));
    try {
      const result = await api.testImageStorageSettings(s.id);
      setTestResults((cur) => ({ ...cur, [s.id]: result }));
    } catch (err) {
      setTestResults((cur) => ({
        ...cur,
        [s.id]: {
          ok: false,
          latency_ms: 0,
          error: err instanceof Error ? err.message : 'Test failed',
        },
      }));
    } finally {
      setTesting((cur) => {
        const { [s.id]: _, ...rest } = cur;
        return rest;
      });
    }
  };

  const handleDelete = async (s: ImageStorageSettings) => {
    if (!window.confirm(`Delete storage config "${s.name || s.bucket}"?`)) return;
    try {
      await api.deleteImageStorageSettings(s.id);
      fetchItems();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const input =
    'mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Image storage</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          S3-compatible bucket where AI-generated page images are uploaded. The
          public URL is written into your deployed pages. Enable AI image
          generation per project under Project → Settings → General.
        </p>
      </div>

      {error && (
        <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-4 py-3">
          {error}
        </div>
      )}

      {/* Existing configs */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Your buckets</h3>
          <button
            onClick={openCreate}
            className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700"
          >
            Add bucket
          </button>
        </div>
        {loading ? (
          <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400">Loading…</div>
        ) : items.length === 0 ? (
          <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400">
            No storage configured yet. Add a bucket to enable image generation.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {items.map((s) => {
              const result = testResults[s.id];
              return (
                <li key={s.id} className="px-4 py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {s.name || s.bucket}
                      {!s.is_enabled && (
                        <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">(disabled)</span>
                      )}
                      {s.is_system && (
                        <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">system</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {s.bucket}
                      {s.endpoint_url ? ` · ${s.endpoint_url}` : ' · AWS S3'}
                    </div>
                    {result && (
                      <div
                        className={`mt-1 text-xs ${
                          !result.ok
                            ? 'text-red-600 dark:text-red-400'
                            : result.public_readable
                              ? 'text-green-700 dark:text-green-300'
                              : 'text-amber-600 dark:text-amber-400'
                        }`}
                      >
                        {!result.ok
                          ? `Failed: ${result.error}`
                          : result.public_readable
                            ? `OK - files are publicly viewable (${result.latency_ms} ms)`
                            : `Connected, but NOT publicly readable. ${result.error || ''}`}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleTest(s)}
                    disabled={!!testing[s.id]}
                    className="px-2.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                  >
                    {testing[s.id] ? 'Testing…' : 'Test'}
                  </button>
                  <button
                    onClick={() => openEdit(s)}
                    className="px-2.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(s)}
                    className="px-2.5 py-1 text-xs border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/30"
                  >
                    Delete
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Create / edit form */}
      {showForm && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-5 space-y-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {editingId === null ? 'Add bucket' : 'Edit bucket'}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Label</label>
              <input
                className={input}
                value={form.name ?? ''}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Cloudflare R2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Bucket *</label>
              <input
                className={input}
                value={form.bucket}
                onChange={(e) => setForm({ ...form, bucket: e.target.value })}
                placeholder="my-page-images"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Endpoint URL
            </label>
            <input
              className={input}
              value={form.endpoint_url ?? ''}
              onChange={(e) => setForm({ ...form, endpoint_url: e.target.value })}
              placeholder="https://<account>.r2.cloudflarestorage.com (blank for AWS S3)"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Access key ID *
              </label>
              <input
                className={input}
                value={form.access_key_id}
                onChange={(e) => setForm({ ...form, access_key_id: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Secret access key {editingId === null ? '*' : ''}
              </label>
              <input
                type="password"
                className={input}
                value={form.secret_access_key}
                onChange={(e) =>
                  setForm({ ...form, secret_access_key: e.target.value })
                }
                placeholder={editingId === null ? '' : 'leave blank to keep current'}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Region</label>
              <input
                className={input}
                value={form.region ?? ''}
                onChange={(e) => setForm({ ...form, region: e.target.value })}
                placeholder="auto"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Public base URL
              </label>
              <input
                className={input}
                value={form.public_base_url ?? ''}
                onChange={(e) =>
                  setForm({ ...form, public_base_url: e.target.value })
                }
                placeholder="https://images.your-domain.com (optional CDN)"
              />
              {(form.endpoint_url || '').includes('r2.cloudflarestorage.com') &&
              !(form.public_base_url || '').trim() ? (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                  Required for Cloudflare R2: the S3 endpoint isn't public. Set a
                  custom domain or your <code>pub-*.r2.dev</code> URL, or images
                  won't load on deployed pages.
                </p>
              ) : (
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  Optional for public S3/Spaces/B2 buckets (the URL is derived).
                  Required for Cloudflare R2 - use a custom domain or r2.dev URL.
                </p>
              )}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_enabled ?? true}
              onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })}
              className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 dark:text-indigo-400 focus:ring-indigo-500"
            />
            <span className="text-gray-700 dark:text-gray-300">Enabled</span>
          </label>

          {/* Upload optimization */}
          <div className="border-t border-gray-100 dark:border-gray-700 pt-3 space-y-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.optimize_uploads ?? true}
                onChange={(e) => setForm({ ...form, optimize_uploads: e.target.checked })}
                className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 dark:text-indigo-400 focus:ring-indigo-500"
              />
              <span className="text-gray-700 dark:text-gray-300">Optimize uploads (resize + re-encode)</span>
            </label>
            <p className="text-xs text-gray-400 dark:text-gray-500 -mt-1">
              Uploaded images are resized to the widths below and re-encoded -
              smaller files, faster pages. Runs in the background after upload.
            </p>
            {form.optimize_uploads && (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Format</label>
                  <select
                    className={input}
                    value={form.image_format ?? 'webp'}
                    onChange={(e) => setForm({ ...form, image_format: e.target.value })}
                  >
                    <option value="webp">WebP (recommended)</option>
                    <option value="jpeg">JPEG</option>
                    <option value="keep">Keep original</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Quality</label>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    className={input}
                    value={form.image_quality ?? 82}
                    onChange={(e) =>
                      setForm({ ...form, image_quality: Number(e.target.value) || 82 })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Widths (px)</label>
                  <input
                    className={input}
                    value={(form.variant_widths ?? []).join(', ')}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        variant_widths: e.target.value
                          .split(',')
                          .map((w) => parseInt(w.trim(), 10))
                          .filter((w) => Number.isFinite(w) && w > 0),
                      })
                    }
                    placeholder="480, 768, 1024, 1536"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
            <button
              onClick={() => setShowForm(false)}
              className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded hover:bg-indigo-700 disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Provider help panel */}
      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Don't have a bucket? Pick a provider
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          All of these speak the S3 API - sign up, create a bucket + access key,
          then paste the endpoint, bucket, and credentials above.
        </p>
        <ul className="mt-3 space-y-3">
          {PROVIDERS.map((p) => (
            <li
              key={p.name}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-3 text-sm"
            >
              <div className="flex items-center gap-2">
                <a
                  href={p.signupUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  {p.name}
                </a>
                {p.recommended && (
                  <span className="text-xs bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 px-1.5 py-0.5 rounded">
                    Recommended
                  </span>
                )}
              </div>
              <p className="text-gray-600 dark:text-gray-300 mt-1">{p.pricing}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 font-mono">
                endpoint: {p.endpointHint}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
